import { Fragment, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { AppBar, Box, css, FormControlLabel, IconButton, Stack, Switch, ThemeProvider, Toolbar, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGeopostThemeAppBar } from 'theme';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { useTheme } from '@emotion/react';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import LogoutIcon from '@mui/icons-material/Logout';
import { shallow } from 'zustand/shallow';
import StraightenIcon from '@mui/icons-material/Straighten';
import SsidChartIcon from '@mui/icons-material/SsidChart';

import { useSessionStore } from 'session/useSessionStore';
import { SeismicShowCoordinates } from './SeismicShowCoordinates';
import { SeismicLayerLoadingProgress } from './SeismicLayerLoadingProgress';
import { SeismicNavbarTraceHeader } from './SeismicNavbarTraceHeader';
import { GeopostPopup } from 'components/geopostPopup/GeopostPopup';
import { ColorBarLegend } from './ColorBarLegend';
import { useMapStore } from '../stores/useMapStore';
import { SeismicGraticule } from './SeismicGraticule';
import { SeismicSelectorLine } from './SeismicSelectorLine';
import { SeismicLogoNavbar } from './SeismicLogoNavbar';
import { IRelatedDataImages, useSeismicStore } from '../stores/useSeismicStore';
import { SeismicMetadataNavbar } from './metadata/SeismicMetadataNavbar';
import { SeismicMetadataPopup } from './metadata/SeismicMetadataPopup';
import { SeismicColorbarNavbar } from './SeismicColorbarNavbar';
import { SeismicScaleNavbar } from './SeismicScaleNavbar';
import { SeismicProjections } from './projections/SeismicProjections';
import { SeismicMiniMap } from './worldMap/SeismicMiniMap';
import { SeismicMiniMapPopup } from './worldMap/SeismicMiniMapPopup';
import { SeismicAttributes } from './attributes/SeismicAttributes';
import { SeismicWell } from './wells/SeismicWell';
import { SeismicWellPopup } from './wells/SeismicWellPopup';
import { SeismicWellRelatedDataPopup } from './wells/SeismicWellRelatedDataPopup';
import { SeismicImagePopup } from './wells/SeismicImagePopup';
import { SeismicWellHoverInfo } from './wells/SeismicWellHoverInfo';
import { IRelatedData } from '../models/interfaces/IRelatedData';
import { SeismicInterpretationNavbar } from './interpretation/SeismicInterpretationNavbar';
import { SeismicWebTour } from './webTour/SeismicWebTour';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { SurveyType } from '../models/enums/SurveyType';
import { SeismicInterpretation2DViewer } from './interpretation/components/SeismicInterpretation2DViewer';
import { AlinePopup } from 'features/tenant-customized/components/AlinePopup';
import { GainMenu2D } from './GainMenu2D';
import { SeismicAttributesPopup } from './attributes/SeismicAttributesPopup';
import { SeismicAttribute } from '../models/interfaces/ISurveyMetadata';
import { GeopostMuiPopup } from 'components/geopostMuiPopup/GeopostMuiPopup';
import { PerformancePanel } from './performancePanel/PerformancePanel';
import { getParamsFromUrl } from '../utils/seismicUrlUtils';
import { SeismicLine2DSelector } from './line2dSelector/SeismicLine2DSelector';
import { useLoadingTileStore } from '../stores/useLoadingTilesStore';
import { SeismicKeyboard } from './keyboard/components/SeismicKeyboard';
import { LineType } from '../models/enums/LineType';
import { useLine3DNavigationStore } from '../stores/useLine3DNavigationStore';
import { SeismicNavbarLoadingIcon } from './SeismicNavbarLoadingIcon';
import { LogoutButton } from './LogoutButton';
import { SeismicAntialiasNavbar } from './SeismicAntialiasNavbar';
import { useMapSignalR } from '../hooks/useMapSignalR';
import { Position } from 'components/geopostPopup/models/enums/Position';
import { LinkTo3DViewer } from './LinkTo3DViewer';
import { MapSignalRWatcher } from './MapSignalRWatcher';
import { SeismicScaleBar } from './SeismicScaleBar';
import { ScaleBars2D } from './ScaleBars2D';
import { GeopostPopover } from 'components/GeopostPopover';
import { DefaultScaleMenu } from './DefaultScaleMenu';
import { FrequencySpectrumMenu } from './FrequencySpectrumMenu';
import { AmplitudeDomain } from '../models/enums/AmplitudeDomain';
import { TraceSortFilterSelector } from './prestack/TraceSortFilterSelector';
import FrequencySpectrum from 'components/icons/components/FrequencySpectrum';
import { ResetViewButton } from './ResetViewButton';

const styles = css({
    '.container-loading': {
        position: 'absolute',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        background: '#00000099',
        zIndex: 9,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        '& > *': {
            animation: 'fade 2s linear',
            animationIterationCount: 'infinite'
        }
    },
    '.MuiToolbar-root': {
        paddingTop: '5px',
        minHeight: '56px'
    },
    '& .MuiToolbar-root > *': {
        margin: 0,
    },
    '.link-button': {
        cursor: 'pointer',
        textDecoration: 'none',
        marginLeft: '10px',
        marginRight: '10px',
        color: '#fff'
    },
    '.link-button:hover': {
        color: '#ccc',
        textDecoration: 'underline'
    },
    '@keyframes fade': {
        '0%,100%': { opacity: 0},
        '50%': { opacity: 1}
    }
});

export function SeismicNavbar() {
    const { t } = useTranslation();
    const themeAppBar = useGeopostThemeAppBar();
    const { mapSize, swipeSeismic, geopostMap } = useMapStore(state => ({
        mapSize: state.mapSize,
        swipeSeismic: state.swipeSeismic,
        geopostMap: state.map
    }), shallow);
    const theme = useTheme();
    const { setSortOption, sortOption, hasErrorOnGeometry, surveyMetadata, volumeTokenSwipe, storeWellRelatedData, wellHoverInfo,
        geometryLine: dataGeometryLine, restrict, navbarLoading, colorbarLegendWidth} = useSeismicStore(state => ({
        restrict: state.restrict,
        hasErrorOnGeometry: state.hasErrorOnGeometry,
        geometryLine: state.geometryLine,
        surveyMetadata: state.surveyMetadata,
        storeWellRelatedData: state.wellRelatedData,
        setSortOption: state.setSortOption,
        sortOption: state.sortOption,
        wellHoverInfo: state.wellHoverInfo,
        volumeTokenSwipe: state.volumeTokenSwipe,
        navbarLoading: state.navbarLoading,
        colorbarLegendWidth: state.colorbarLegendWidth,
    }), shallow);

    useEffect(() => {console.log('COLORBAR LEGEND WIDTH ================>', colorbarLegendWidth)}, [colorbarLegendWidth]);

    const { tenantConfig } = useSessionStore(state => ({
        tenantConfig: state.tenantConfiguration,
    }), shallow);

    const { current: urlParams } = useRef(getParamsFromUrl());

    const [openedMetadata, setOpenedMetadata] = useState(false);
    const [openedLabelColorbar, setOpenedLabelColorbar] = useState(false);
    const [openedMiniMap, setOpenedMiniMap] = useState(false);
    const [openedInterpretation, setOpenedInterpretation] = useState((false || !!urlParams.interpretationToken));
    const [openedWell, setOpenedWell] = useState(false);
    const [openedAttributes, setOpenedAttributes] = useState(false);
    const [openPerformanceMonitor, setOpenPerformanceMonitor] = useState(false);
    const [ frequencySpectrumMenuOpened, setFrequencySpectrumMenuOpened ] = useState(false);

    const selectedColorbar = useSeismicStore(state => state.colorbar);

    const [seismicAttributes, setSeismicAttributes] = useState<{
        baseProductName: string,
        all: SeismicAttribute[]
    } | undefined>(undefined);

    const updateSwipe = (value: number) => {
        if (geopostMap && swipeSeismic && volumeTokenSwipe !== '-') {
            swipeSeismic.updateParams({
                swipeValue: value
            });
            geopostMap.render();
        }
    };

    const wellRelatedData = useRef<{
        [key: string]: {
            layer: VectorLayer<VectorSource>;
            relatedData: IRelatedData;
            opened: boolean;
            images: IRelatedDataImages;
        }
    } | null>(null);

    useEffect(() => {
        if (storeWellRelatedData) {
            wellRelatedData.current = storeWellRelatedData;
        }
    }, [storeWellRelatedData]);

    // useFeatureHoverWells(wellRelatedData);

    const { featureFlags } = useFeatureFlags();

    useEffect(() => {
        if (!!featureFlags) {
            if (featureFlags.seismic2D.DefaultMiniMapOn) {
                setOpenedMiniMap(true);
            }
        };
    }, [featureFlags]);

    let surveyName = '';

    if (surveyMetadata && surveyMetadata.DisplayName && surveyMetadata.DisplayName.trim()) {
        surveyName = surveyMetadata.DisplayName;

        if (surveyName.length > 45) {
            surveyName = surveyName.substring(0, 45) + '...';
        }
    }

    const lineType = useLine3DNavigationStore(state => state.lineType);

    const hasErrorInMetadata = surveyMetadata?.Error;

    const defaultColorbarLegendOn = useMemo(() => featureFlags?.seismic2D.DefaultColorbarLegendOn, [featureFlags]);

    const iconButtonRef = useRef<HTMLButtonElement | null>(null);
    const [ showVerticalBar, setShowVerticalBar ] = useState(false);
    const [ showHorizontalBar, setShowHorizontalBar ] = useState(false);
    const [ popoverAnchor, setPopoverAnchor ] = useState<HTMLButtonElement | null>(null);

    const enableFrequencySpectrum = useMemo(() => featureFlags?.seismic2D.FrequencySpectrum && surveyMetadata?.Domain === AmplitudeDomain.Time && lineType !== LineType.ZSlice, [featureFlags?.seismic2D.FrequencySpectrum, lineType, surveyMetadata?.Domain]);

    useEffect(() => {
        if (defaultColorbarLegendOn) {
            setOpenedLabelColorbar(defaultColorbarLegendOn);
            console.log('caiu no fluxo default colorbar on');
        }
    }, [defaultColorbarLegendOn]);

    return (
        <Fragment>
            <AppBar position='static' css={styles}>
                <Toolbar style={{ paddingLeft: '15px', paddingRight: '20px' }}>
                    <SeismicNavbarLoadingIcon show={navbarLoading}/>
                    <ThemeProvider theme={themeAppBar}>
                        <Stack direction='row' justifyContent={'space-between'} alignItems='center' width='100%'>
                            <Stack direction='row' style={{width: '80%'}}>
                                <Stack alignItems='center' justifyContent='center' sx={{marginRight: '15px'}}>
                                    <SeismicLogoNavbar />
                                </Stack>
                                {featureFlags?.seismic2D.WebTour && !hasErrorInMetadata && <SeismicWebTour />}
                                {tenantConfig?.appBar.displayNamePosition === 'left' ?
                                    <Stack sx={{maxWidth:'285px'}} alignItems='center' justifyContent='center'>
                                        <Typography title={surveyMetadata?.DisplayName ?? ''} style={{ marginLeft: 10, marginRight: 25, fontSize: 12, width:'285px', maxWidth: '285px', whiteSpace: 'nowrap' }}>
                                            {surveyName}
                                        </Typography>
                                    </Stack>
                                    : null}
                                {featureFlags?.seismic2D.EnableParentAppLink && tenantConfig &&
                                    <Stack justifyContent='center'>
                                        <a className='link-button' target='_blank' href={tenantConfig.parentApplicationHost} title={tenantConfig.parentApplicationName}>{tenantConfig.parentApplicationName}</a>
                                    </Stack>
                                }
                                <ResetViewButton/>
                                { !hasErrorInMetadata && <SeismicMetadataNavbar opened={openedMetadata} setOpened={setOpenedMetadata} />}
                                {featureFlags?.seismic2D.ColorBar && !hasErrorInMetadata && <SeismicColorbarNavbar openedLegendColorbar={openedLabelColorbar} setOpenedLegendColorbar={setOpenedLabelColorbar} />}
                                {!hasErrorInMetadata && <SeismicScaleNavbar canFit={featureFlags?.seismic2D.AllowHorizontalFit ?? false} />}
                                {
                                    featureFlags?.seismic2D.ScaleBar
                                    &&
                                    <IconButton ref={iconButtonRef} onClick={() => setPopoverAnchor(iconButtonRef.current)} color='primary'>
                                        <StraightenIcon/>
                                    </IconButton>
                                }
                                {featureFlags?.seismic2D.Antialias && <SeismicAntialiasNavbar/>}
                                {featureFlags?.seismic2D.LineSelector2D &&  !hasErrorInMetadata && surveyMetadata?.Type === SurveyType.Seismic2D && <SeismicLine2DSelector />}
                                {featureFlags?.seismic2D.LineType && !hasErrorInMetadata && <Stack alignItems='center' justifyContent='center'><SeismicSelectorLine featureFlags={featureFlags?.seismic2D} /></Stack>}
                                <ThemeProvider theme={theme}>
                                    {featureFlags?.seismic2D.Gain && !hasErrorInMetadata && <GainMenu2D/>}
                                </ThemeProvider>
                                {featureFlags?.seismic2D.TraceHeader && !hasErrorInMetadata && <SeismicNavbarTraceHeader />}
                                {featureFlags?.seismic2D.Graticule && !hasErrorInMetadata && <SeismicGraticule dataGeometryLine={dataGeometryLine} />}
                                {featureFlags?.seismic2D.Projections && !hasErrorInMetadata && restrict?.SeismicCanDisplayProjections && <SeismicProjections />}
                                {featureFlags?.seismic2D.Attributes && !hasErrorInMetadata && restrict?.SeismicDisplayPhysicalAttributes && <SeismicAttributes setOpenedPopup={setOpenedAttributes} setSelectedAttributes={setSeismicAttributes} alineFeatureFlags={featureFlags?.seismic2D.AlineWorkflow}/>}
                                {featureFlags?.seismic2D.EnableWells && !hasErrorInMetadata && <SeismicWell opened={openedWell} setOpened={setOpenedWell} dataGeometryLine={dataGeometryLine} />}
                                {featureFlags?.seismic2D.Interpretation && !hasErrorInMetadata && <SeismicInterpretationNavbar opened={openedInterpretation} setOpened={setOpenedInterpretation} />}
                                {featureFlags?.seismic2D.Minimap && restrict?.SeismicCanDisplayNavigationMap && !hasErrorInMetadata && <SeismicMiniMap hasErrorOnGeometry={hasErrorOnGeometry} opened={openedMiniMap} setOpened={setOpenedMiniMap} dataGeometryLine={dataGeometryLine} />}
                                {featureFlags?.seismic2D.LinkTo3D && surveyMetadata?.Type === SurveyType.Seismic3D && <LinkTo3DViewer/>}
                                {featureFlags?.seismic2D.DefaultScaleMenu && !hasErrorInMetadata && <DefaultScaleMenu/>}
                                { enableFrequencySpectrum && <IconButton title='Frequency Spectrum Chart' onClick={() => setFrequencySpectrumMenuOpened(!frequencySpectrumMenuOpened)}><FrequencySpectrum color='white' /></IconButton> }
                                {
                                    urlParams.showSortOption ?
                                    /*
                                        <select
                                            title='Select the data sort'
                                            style={{marginLeft: '5px', marginRight: '5px', padding: '3px', width: '150px', borderRadius: '5px'}}
                                            value={sortOption}
                                            onChange={(evt) => setSortOption(evt.target.value)}>
                                            <option value='trace_index'>Trace Index</option>
                                            <option value='shotpoint,channel_number'>Shot + Channel Number</option>
                                            <option value='cdp,channel_number'>CDP + Channel Number</option>
                                            <option value='channel_number,cdp'>Channel Number + CDP</option>
                                        </select>
                                        */
                                        <TraceSortFilterSelector
                                            sortOption={sortOption}
                                            onSortChange={function (sort: string): void {
                                                setSortOption(sort);
                                            } }
                                        />
                                        : null
                                }
                                <ThemeProvider theme={theme}>
                                    {featureFlags?.seismic2D.AlineWorkflow && <AlinePopup></AlinePopup>}
                                </ThemeProvider>
                                {
                                    featureFlags?.seismic2D.PerformanceMonitor
                                    &&
                                    urlParams.performanceMonitoring
                                    &&
                                    <IconButton sx={{color: 'white'}} onClick={() => setOpenPerformanceMonitor(current => !current)} title='Performance monitor'>
                                        <QueryStatsIcon/>
                                    </IconButton>
                                }
                            </Stack>
                            <Stack direction={'row'} spacing={2}>
                                {
                                    (!!featureFlags?.seismic2D.Logout)
                                    &&
                                    <LogoutButton/>
                                }
                                {!tenantConfig?.appBar.displayNamePosition || tenantConfig?.appBar.displayNamePosition === 'right' ?
                                    <Stack sx={{maxWidth:'800px'}} alignItems='center' justifyContent='center'>
                                        <Typography title={surveyMetadata?.DisplayName ?? ''} style={{ fontSize: 12, maxWidth: '800px', width:'auto', textAlign:'center'}}>
                                            {surveyName}
                                        </Typography>
                                    </Stack>
                                    : null}
                                {featureFlags?.seismic2D.Depth &&
                                    <Box component='div' style={{ float: 'right', alignItems: 'center', justifyContent:'center', display:'flex' }} >
                                        <SeismicShowCoordinates />
                                    </Box>
                                }
                            </Stack>
                        </Stack>
                    </ThemeProvider>
                </Toolbar>
            </AppBar>
            <SeismicLayerLoadingProgress />

            <SeismicMetadataPopup opened={openedMetadata} setOpened={setOpenedMetadata} volumesMetadata={!!surveyMetadata ? [surveyMetadata] : []} />
            <SeismicMiniMapPopup opened={openedMiniMap} setOpened={setOpenedMiniMap} dataGeometryLine={dataGeometryLine} hasGeometryError={hasErrorOnGeometry}/>
            <SeismicInterpretation2DViewer opened={openedInterpretation} setOpened={setOpenedInterpretation} dataGeometryLine={dataGeometryLine} />
            <SeismicWellPopup opened={openedWell} surveyType={SurveyType.Seismic2D} setOpened={setOpenedWell} dataGeometryLine={dataGeometryLine ? dataGeometryLine : { Line: '', Max: 100, Min: 0, TotalTraces: 100 }} />
            {Object.keys(storeWellRelatedData).map((key) => (
                <Fragment key={key}>
                    <SeismicWellRelatedDataPopup key={key} myKey={key} data={storeWellRelatedData[key]} />
                    {Object.keys(storeWellRelatedData[key].images).map((imageKey) => (
                        <SeismicImagePopup key={imageKey} parentKey={key} parentData={storeWellRelatedData[key]} imageKey={imageKey} />
                    ))}
                </Fragment>
            ))}
            {wellHoverInfo.created &&
                <SeismicWellHoverInfo data={wellHoverInfo} />
            }
            <SeismicAttributesPopup opened={openedAttributes} setOpened={setOpenedAttributes} seismicAttributes={seismicAttributes} />

            <GeopostPopup
                temporaryIgnoreFlexInContainer
                open={openedLabelColorbar}
                title={selectedColorbar.Label}
                onChangeOpen={value => setOpenedLabelColorbar(value)}
                overlay={false}
                height={500}
                width={colorbarLegendWidth + 'px'}
                minWidth={colorbarLegendWidth }
                padding={'10px'}
                position={Position.middleLeft}
            >
                <ColorBarLegend />
            </GeopostPopup>
            {
                volumeTokenSwipe !== '-' ?
                    <Box component='div'>
                        <input id="swipe" onChange={(evt) => updateSwipe(parseInt(evt.target.value))} type="range" style={{ width: '100%' }} />
                    </Box>
                    : null
            }

            {
                featureFlags?.seismic2D.PerformanceMonitor
                &&
                urlParams.performanceMonitoring
                &&
                <GeopostPopup
                    open={openPerformanceMonitor}
                    onChangeOpen={(opened) => !opened && setOpenPerformanceMonitor(false)}
                    height={600}
                    width={1450}
                    position={Position.middleLeft}
                    title="Performance monitor"
                    temporaryIgnoreFlexInContainer={false}
                    overlay={false}
                >
                    <PerformancePanel surveyType={SurveyType.Seismic2D}/>
                </GeopostPopup>
            }
            {featureFlags?.seismic2D.Shortcuts && <SeismicKeyboard />}
            {
                featureFlags?.seismic2D.SignalR
                &&
                <MapSignalRWatcher/>
            }
            <GeopostPopover
                onClose={() => setPopoverAnchor(null)}
                anchorEl={popoverAnchor}
            >
                <Stack
                    gap={1}
                >
                    <FormControlLabel control={<Switch checked={showVerticalBar} onChange={() => setShowVerticalBar(!showVerticalBar)}/> } label={<Typography color='primary'>Vertical</Typography>}/>
                    <FormControlLabel control={<Switch checked={showHorizontalBar} onChange={() => setShowHorizontalBar(!showHorizontalBar)}/> } label={<Typography color='primary'>Horizontal</Typography>}/>
                </Stack>

            </GeopostPopover>
            {
                enableFrequencySpectrum
                &&
                <GeopostPopup
                    open={frequencySpectrumMenuOpened}
                    title='Frequency Spectrum'
                    onChangeOpen={(value) => setFrequencySpectrumMenuOpened(value)}
                    temporaryIgnoreFlexInContainer
                    height={'55vh'}
                    width={'31vw'}
                    position={Position.topRight}
                    overlay={false}
                >
                    <FrequencySpectrumMenu/>
                </GeopostPopup>
            }

            {<ScaleBars2D showHorizontalBar={showHorizontalBar} showVerticalBar={showVerticalBar} />}

        </Fragment>
    );
}
